import { v4 as uuidv4 } from 'uuid';

class ComprehensiveAnalytics {
  constructor() {
    this.sessionId = uuidv4();
    this.userId = this.getOrCreateUserId();
    this.sessionStart = Date.now();
    this.events = [];
    this.userAgent = navigator.userAgent;
    this.isInitialized = false;
    
    // Initialize analytics
    this.init();
  }

  init() {
    this.collectDeviceInfo();
    this.collectLocationInfo();
    this.setupEventListeners();
    this.startSessionTracking();
    this.isInitialized = true;
    
    // Track initial page load
    this.trackEvent('page_load', {
      page: window.location.pathname,
      referrer: document.referrer || 'direct',
      timestamp: Date.now()
    });
  }

  getOrCreateUserId() {
    let userId = localStorage.getItem('analytics_user_id');
    if (!userId) {
      userId = uuidv4();
      localStorage.setItem('analytics_user_id', userId);
      localStorage.setItem('analytics_first_visit', Date.now());
    }
    return userId;
  }

  async collectLocationInfo() {
    try {
      // Cache by session so we don’t hammer the free endpoint on every navigation
      const cached = sessionStorage.getItem('analytics_location_info');
      let locationData;

      if (cached) {
        locationData = JSON.parse(cached);
      } else {
        const response = await fetch('https://ipapi.co/json/');
        locationData = await response.json();
        sessionStorage.setItem('analytics_location_info', JSON.stringify(locationData));
      }
      
      this.locationInfo = {
        country: locationData.country_name,
        region: locationData.region,
        city: locationData.city,
        timezone: locationData.timezone,
        isp: locationData.org,
        ip: locationData.ip
      };
      
      // Optional precise geolocation (disabled by default).
      // If you really need street-level accuracy set
      //   REACT_APP_PRECISE_GEO=true
      // in your environment and visitors who grant permission will provide
      // coordinates. Otherwise only IP-based city/region data is collected
      // and no browser prompt is shown.
      const preciseAllowed = process.env.REACT_APP_PRECISE_GEO === 'true';

      if (preciseAllowed && navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            this.locationInfo.precise = {
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
              accuracy: position.coords.accuracy
            };
          },
          () => {
            // User denied location access – ignore.
          },
          { maximumAge: 60_000, timeout: 5000 }
        );
      }
    } catch (error) {
      console.log('Could not fetch location data:', error);
    }
  }

  collectDeviceInfo() {
    this.deviceInfo = {
      screen: {
        width: window.screen.width,
        height: window.screen.height,
        availWidth: window.screen.availWidth,
        availHeight: window.screen.availHeight,
        colorDepth: window.screen.colorDepth,
        pixelDepth: window.screen.pixelDepth
      },
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight
      },
      devicePixelRatio: window.devicePixelRatio,
      language: navigator.language,
      languages: navigator.languages,
      platform: navigator.platform,
      cookieEnabled: navigator.cookieEnabled,
      onLine: navigator.onLine,
      userAgent: navigator.userAgent,
      vendor: navigator.vendor,
      deviceType: this.getDeviceType(),
      browser: this.getBrowserInfo(),
      os: this.getOSInfo(),
      connection: this.getConnectionInfo()
    };
  }

  getDeviceType() {
    const ua = navigator.userAgent;
    if (/tablet|ipad|playbook|silk/i.test(ua)) {
      return 'tablet';
    }
    if (/mobile|iphone|ipod|android|blackberry|opera|mini|windows\sce|palm|smartphone|iemobile/i.test(ua)) {
      return 'mobile';
    }
    return 'desktop';
  }

  getBrowserInfo() {
    const ua = navigator.userAgent;
    let browser = 'unknown';
    let version = 'unknown';

    if (ua.includes('Chrome') && !ua.includes('Edg')) {
      browser = 'Chrome';
      version = ua.match(/Chrome\/(\d+)/)?.[1] || 'unknown';
    } else if (ua.includes('Safari') && !ua.includes('Chrome')) {
      browser = 'Safari';
      version = ua.match(/Version\/(\d+)/)?.[1] || 'unknown';
    } else if (ua.includes('Firefox')) {
      browser = 'Firefox';
      version = ua.match(/Firefox\/(\d+)/)?.[1] || 'unknown';
    } else if (ua.includes('Edg')) {
      browser = 'Edge';
      version = ua.match(/Edg\/(\d+)/)?.[1] || 'unknown';
    }

    return { browser, version };
  }

  getOSInfo() {
    const ua = navigator.userAgent;
    let os = 'unknown';
    let version = 'unknown';

    if (ua.includes('Windows')) {
      os = 'Windows';
      if (ua.includes('Windows NT 10.0')) version = '10';
      else if (ua.includes('Windows NT 6.3')) version = '8.1';
      else if (ua.includes('Windows NT 6.2')) version = '8';
      else if (ua.includes('Windows NT 6.1')) version = '7';
    } else if (ua.includes('Mac OS X')) {
      os = 'macOS';
      version = ua.match(/Mac OS X (\d+_\d+)/)?.[1]?.replace('_', '.') || 'unknown';
    } else if (ua.includes('Linux')) {
      os = 'Linux';
    } else if (ua.includes('Android')) {
      os = 'Android';
      version = ua.match(/Android (\d+\.\d+)/)?.[1] || 'unknown';
    } else if (ua.includes('iOS') || ua.includes('iPhone') || ua.includes('iPad')) {
      os = 'iOS';
      version = ua.match(/OS (\d+_\d+)/)?.[1]?.replace('_', '.') || 'unknown';
    }

    return { os, version };
  }

  getConnectionInfo() {
    if (typeof navigator !== 'undefined' && navigator.connection) {
      const conn = navigator.connection;
      return {
        effectiveType: conn.effectiveType,
        downlink: conn.downlink,
        rtt: conn.rtt,
        saveData: conn.saveData
      };
    }
    return null;
  }

  setupEventListeners() {
    // Track page visibility changes
    document.addEventListener('visibilitychange', () => {
      this.trackEvent('visibility_change', {
        hidden: document.hidden,
        timestamp: Date.now()
      });
    });

    // Track scroll depth
    let maxScroll = 0;
    window.addEventListener('scroll', () => {
      const scrollPercent = Math.round(
        (window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100
      );
      if (scrollPercent > maxScroll) {
        maxScroll = scrollPercent;
        this.trackEvent('scroll_depth', {
          depth: scrollPercent,
          timestamp: Date.now()
        });
      }
    });

    // Track clicks with detailed information
    document.addEventListener('click', (event) => {
      this.trackEvent('click', {
        element: event.target.tagName,
        className: event.target.className,
        id: event.target.id,
        text: event.target.textContent?.substring(0, 100),
        x: event.clientX,
        y: event.clientY,
        timestamp: Date.now()
      });
    });

    // Track form interactions
    document.addEventListener('input', (event) => {
      if (event.target.tagName === 'INPUT' || event.target.tagName === 'TEXTAREA') {
        this.trackEvent('form_interaction', {
          element: event.target.tagName,
          type: event.target.type,
          name: event.target.name,
          timestamp: Date.now()
        });
      }
    });

    // Track page unload
    window.addEventListener('beforeunload', () => {
      this.endSession();
    });

    // Track resize events
    window.addEventListener('resize', () => {
      this.trackEvent('window_resize', {
        width: window.innerWidth,
        height: window.innerHeight,
        timestamp: Date.now()
      });
    });
  }

  startSessionTracking() {
    // Track session every 30 seconds
    this.sessionInterval = setInterval(() => {
      this.trackEvent('session_ping', {
        duration: Date.now() - this.sessionStart,
        timestamp: Date.now()
      });
    }, 30000);
  }

  trackEvent(eventName, data = {}) {
    const event = {
      event: eventName,
      sessionId: this.sessionId,
      userId: this.userId,
      timestamp: Date.now(),
      url: window.location.href,
      path: window.location.pathname,
      ...data
    };

    this.events.push(event);
    
    // Persist session locally so that the Historical dashboard can show fresh sessions
    if (eventName === 'page_load') {
      try {
        const localSessionsKey = 'analytics_local_sessions';
        const stored = JSON.parse(localStorage.getItem(localSessionsKey) || '[]');
        const sessionSummary = {
          sessionId: event.sessionId,
          userId: event.userId,
          startTime: event.timestamp,
          locationInfo: this.locationInfo,
          deviceInfo: this.deviceInfo,
          summary: `${this.locationInfo?.city || 'Unknown'} - ${this.deviceInfo?.deviceType || 'Unknown'}`
        };

        // Avoid duplicates (by sessionId)
        const exists = stored.some((s) => s.sessionId === sessionSummary.sessionId);
        if (!exists) {
          stored.unshift(sessionSummary);
          localStorage.setItem(localSessionsKey, JSON.stringify(stored.slice(0, 100)));
        }
      } catch (err) {
        // localStorage may be unavailable (e.g., disabled cookies) – swallow errors
        console.warn('[analytics] Unable to write local session', err);
      }
    }

    // Send event to your analytics endpoint
    this.sendEvent(event);
  }

  trackPageView(page) {
    this.trackEvent('page_view', {
      page,
      referrer: document.referrer || 'direct',
      timestamp: Date.now()
    });
  }

  trackUserAction(action, details = {}) {
    this.trackEvent('user_action', {
      action,
      ...details,
      timestamp: Date.now()
    });
  }

  async sendEvent(event) {
    // Add device and location info to each event
    const enrichedEvent = {
      ...event,
      deviceInfo: this.deviceInfo,
      locationInfo: this.locationInfo,
      sessionStart: this.sessionStart,
      firstVisit: localStorage.getItem('analytics_first_visit')
    };

    try {
      // Send to your own analytics endpoint
      await fetch('/api/analytics', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(enrichedEvent)
      });
    } catch (error) {
      // Fallback: store in localStorage for later sending
      const storedEvents = JSON.parse(localStorage.getItem('analytics_events') || '[]');
      storedEvents.push(enrichedEvent);
      localStorage.setItem('analytics_events', JSON.stringify(storedEvents.slice(-100))); // Keep last 100 events
    }

    // Also log to console for development
    if (process.env.NODE_ENV === 'development') {
      console.log('Analytics Event:', enrichedEvent);
    }
  }

  getUserProfile() {
    return {
      userId: this.userId,
      sessionId: this.sessionId,
      deviceInfo: this.deviceInfo,
      locationInfo: this.locationInfo,
      sessionDuration: Date.now() - this.sessionStart,
      eventsCount: this.events.length,
      firstVisit: localStorage.getItem('analytics_first_visit'),
      isReturningUser: localStorage.getItem('analytics_user_id') !== this.userId
    };
  }

  getDetailedStats() {
    const events = this.events;
    const pageViews = events.filter(e => e.event === 'page_view');
    const clicks = events.filter(e => e.event === 'click');
    const scrollEvents = events.filter(e => e.event === 'scroll_depth');
    const maxScroll = Math.max(...scrollEvents.map(e => e.depth), 0);

    return {
      userProfile: this.getUserProfile(),
      sessionStats: {
        duration: Date.now() - this.sessionStart,
        pageViews: pageViews.length,
        totalClicks: clicks.length,
        maxScrollDepth: maxScroll,
        eventsCount: events.length
      },
      recentEvents: events.slice(-10)
    };
  }

  endSession() {
    clearInterval(this.sessionInterval);
    this.trackEvent('session_end', {
      duration: Date.now() - this.sessionStart,
      totalEvents: this.events.length,
      timestamp: Date.now()
    });
  }
}

// Create singleton instance
const analytics = new ComprehensiveAnalytics();

// Export for use in components
export default analytics;

// Helper functions for easy use in components
export const trackPageView = (page) => analytics.trackPageView(page);
export const trackUserAction = (action, details) => analytics.trackUserAction(action, details);
export const trackEvent = (event, data) => analytics.trackEvent(event, data);
export const getUserProfile = () => analytics.getUserProfile();
export const getDetailedStats = () => analytics.getDetailedStats(); 